import React from 'react';
import { TaggedDataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { CompositeDataSetReportInDataSet } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetGenerateReportInsightsResult } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-generate-report-insights-result';
import { usePolling } from 'apps/sigma2-ui/src/lib/hooks/usePolling';
import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { errResult } from 'libs/end-to-end/src/result';
import { okResult } from 'libs/end-to-end/src/result';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { Result } from 'libs/end-to-end/src/result';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { GenericSuccess } from 'libs/end-to-end/src/generic';
import { StartCompositeDataSetGenerateInsightsInput } from 'libs/end-to-end/src/types/databases/data-sets/start-composite-data-set-generate-insights-input';

const getReportInsightsResult = async ({
  userAuthenticatedApiCall,
  organizationId,
  databaseId,
  dataSetId,
}: {
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  organizationId: string;
  databaseId: string;
  dataSetId: string;
}): Promise<
  Result<CompositeDataSetGenerateReportInsightsResult, BaseError>
> => {
  const result = await userAuthenticatedApiCall({
    url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/report-insights`,
    responseSchema: CompositeDataSetGenerateReportInsightsResult,
  });

  if (!result) {
    return errResult({
      errorType: 'UnknownError',
      errorReason: 'Unknown error',
    });
  }

  if (result.resultType === 'err') {
    return result;
  }

  return okResult(result.value);
};

export type UseGenerateReportsInsightsPayload = {
  onGenerateReportsInsights: () => void;
  onCancel: () => void;
  result: CompositeDataSetGenerateReportInsightsResult;
};

export const useGenerateReportsInsights = ({
  organizationId,
  databaseId,
  dataSetId,
  taggedDataSetReferences,
  reports,
}: {
  organizationId: string;
  databaseId: string;
  dataSetId: string;
  taggedDataSetReferences: TaggedDataSetReference[];
  reports: CompositeDataSetReportInDataSet[];
}): UseGenerateReportsInsightsPayload => {
  const { userAuthenticatedApiCall } = useUser();

  const [currentResult, setCurrentResult] =
    useState<CompositeDataSetGenerateReportInsightsResult | null>(null);

  const pollingAction = useMemo(() => {
    return async () => {
      return getReportInsightsResult({
        userAuthenticatedApiCall,
        organizationId,
        databaseId,
        dataSetId,
      });
    };
  }, [organizationId, databaseId, dataSetId, userAuthenticatedApiCall]);

  const onReceivedResult = useCallback(
    (data: Result<CompositeDataSetGenerateReportInsightsResult, BaseError>) => {
      if (data.resultType === 'err') {
        setCurrentResult({
          type: 'error',
          errorReason: 'Error while generating reports insights',
          errorType: 'UnknownError',
        });
        pause();
      } else {
        if (data.value.type === 'error' || data.value.type === 'success') {
          pause();
        }
        setCurrentResult(data.value);
      }
    },
    [setCurrentResult],
  );

  const { pause, resume } = usePolling({
    action: pollingAction,
    onReceivedResult,
    delay: 2000,
    isInitiallyPaused: true,
  });

  const startGenerationPattern = useSimpleWritePattern({
    getUrlFn: () => {
      return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/report-insights`;
    },
    method: 'POST',
    canWrite: true,
    writeSchema: StartCompositeDataSetGenerateInsightsInput,
    responseSchema: GenericSuccess,
  });

  const onGenerateReportsInsights = useCallback(() => {
    if (startGenerationPattern.status !== 'saving') {
      setCurrentResult({
        type: 'loading',
      });
      startGenerationPattern.saveEntity(
        {
          taggedDataSetReferences: taggedDataSetReferences,
          reports: reports,
        },
        () => {
          resume();
        },
        () => {
          pause();
          setCurrentResult({
            type: 'error',
            errorReason: 'Error while starting reports insights',
            errorType: 'UnknownError',
          });
        },
      );
    }
  }, [
    setCurrentResult,
    pause,
    resume,
    startGenerationPattern,
    taggedDataSetReferences,
    reports,
  ]);

  const onCancel = useCallback(() => {
    pause();
  }, [pause]);

  return useMemo(() => {
    return {
      onGenerateReportsInsights,
      onCancel,
      result: currentResult ?? {
        type: 'idle',
      },
    };
  }, [onGenerateReportsInsights, onCancel, currentResult]);
};
